import { loadFirestore } from '@/utils/loadFirestore'
import { artistConfig } from '@/config/artist.config'
import { mark } from '@/utils/perfMarks'
import { restGetDocFields, restString } from '@/utils/firestoreRest'

const DEV_TENANT_KEY = 'gallery_devTenantUid'
const HOST_CACHE_PREFIX = 'tenant:host:'

// Host → uid mappings rarely change. We cache them in localStorage keyed by
// full hostname (subdomain slug today, full custom domain in Phase 6) and
// serve synchronously on boot, revalidating in the background. Saves ~500ms
// of blocking latency on every warm visitor load.
function loadCachedHostUid(hostKey) {
  try {
    const raw = localStorage.getItem(HOST_CACHE_PREFIX + hostKey)
    if (!raw) return null
    return JSON.parse(raw)?.uid || null
  } catch {
    return null
  }
}

function saveCachedHostUid(hostKey, uid) {
  try {
    localStorage.setItem(
      HOST_CACHE_PREFIX + hostKey,
      JSON.stringify({ uid, cachedAt: new Date().toISOString() })
    )
  } catch {
    // Non-critical — next visit will just hit Firestore again.
  }
}

function clearCachedHostUid(hostKey) {
  try {
    localStorage.removeItem(HOST_CACHE_PREFIX + hostKey)
  } catch {
    // ignore
  }
}

// Resolved once at boot by resolveTenant(). Null means "not yet resolved"
// or "no tenant found". Use _tenantResolved to distinguish.
let _resolvedTenantUid = null
let _tenantResolved = false

// Promise that resolves once resolveTenant() completes.
// Router guards can await this before checking isTenantHost().
let _resolveTenantReady
export const tenantReadyPromise = new Promise(resolve => {
  _resolveTenantReady = resolve
})

// Hosts where the bare hostname = platform (no tenant).
// Subdomains of these hosts are tenant subdomains.
export const PLATFORM_HOSTS = ['localhost', 'vitrinis.com', 'nip.io']
// The real production base - where tenant subdomains actually live.
export const PRODUCTION_HOST = 'vitrinis.com'

// Extract the subdomain from a hostname, or null if it's the platform root.
//   andrew.localhost      → 'andrew'
//   andrew.vitrinis.com   → 'andrew'
//   localhost              → null
//   vitrinis.com           → null
//   127.0.0.1              → null
export function extractSubdomain(hostname) {
  if (!hostname) return null
  // IP addresses have no subdomain
  if (/^\d+\.\d+\.\d+\.\d+$/.test(hostname)) return null

  for (const base of PLATFORM_HOSTS) {
    if (hostname === base) return null
    if (hostname.endsWith('.' + base)) {
      const sub = hostname.slice(0, -(base.length + 1))
      // Single-level subdomains (no dots); for nip.io take only the first segment
      // to support local dev via vangogh.192.168.x.x.nip.io
      if (!sub.includes('.')) return sub
      if (base === 'nip.io') return sub.split('.')[0]
    }
  }
  // Unknown host — could be a custom domain (future Phase 6).
  // For now, return null.
  return null
}

// Classify the current hostname into a lookup key (the hostnames/{hostKey} doc id,
// also the localStorage cache key). Returns null for platform hosts (no tenant).
function resolveHostKey(hostname) {
  const subdomain = extractSubdomain(hostname)
  if (subdomain) return subdomain

  // Custom domain: any hostname that isn't a platform host or known subdomain.
  // Lookup lives in hostnames/{hostname} with type: 'custom'.
  if (hostname && !PLATFORM_HOSTS.includes(hostname) && !/^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
    return hostname
  }

  return null
}

// SDK read of hostnames/{hostKey} - lazy import keeps Firestore out of the
// visitor critical path (this only runs for cache validation or as last fallback).
async function sdkGetHostUid(hostKey) {
  const { db, doc, getDoc } = await loadFirestore()
  const snap = await getDoc(doc(db, 'hostnames', hostKey))
  return snap.exists() ? snap.data().uid : null
}

// Resolve the tenant UID from the current hostname.
// Call once at app boot, before loadArtistConfig / initializeWorks.
// After this resolves, getTenantUid() returns the correct value synchronously.
export async function resolveTenant() {
  mark('tenant:resolve:start')
  const hostKey = resolveHostKey(window.location.hostname)

  if (!hostKey) {
    _tenantResolved = true
    _resolveTenantReady()
    mark('tenant:resolve:end')
    return _resolvedTenantUid
  }

  // Warm fast path: serve the cached uid synchronously, validate in the background
  // (reload on mismatch).
  const cachedUid = loadCachedHostUid(hostKey)
  if (cachedUid) {
    _resolvedTenantUid = cachedUid
    _tenantResolved = true
    _resolveTenantReady()
    mark('tenant:resolve:end')

    sdkGetHostUid(hostKey)
      .then(serverUid => {
        if (serverUid === cachedUid) return
        if (serverUid) saveCachedHostUid(hostKey, serverUid)
        else clearCachedHostUid(hostKey)
        window.location.reload()
      })
      .catch(() => {
        // Network failure — cached value stays valid for this session.
      })

    return _resolvedTenantUid
  }

  // Cache miss - lookup ladder, cheapest first; each failure falls through to the next.
  try {
    let uid = null

    // 1. Parked /__site bundle (boot prefetch, no request). Don't consume it -
    //    restGetSiteBundle reuses the same promise.
    try {
      if (window.__siteBundle) {
        uid = (await window.__siteBundle)?.uid ?? null
      }
    } catch { /* fall through */ }

    // 2. /__tenant Worker endpoint: same-origin, edge-cached; absent in dev.
    if (uid == null) {
      try {
        const res = await fetch('/__tenant')
        if (res.ok) uid = (await res.json()).uid ?? null
      } catch { /* fall through */ }
    }

    // 3. Firestore REST, SDK last. REST 404 = no tenant; only REST failure asks the SDK.
    if (uid == null) {
      try {
        const fields = await restGetDocFields(`hostnames/${hostKey}`)
        uid = fields ? restString(fields, 'uid') : null
      } catch {
        uid = await sdkGetHostUid(hostKey)
      }
    }

    if (uid) {
      _resolvedTenantUid = uid
      saveCachedHostUid(hostKey, uid)
    }
  } catch (err) {
    console.error('Tenant resolution failed:', err)
  }

  _tenantResolved = true
  _resolveTenantReady()
  mark('tenant:resolve:end')
  return _resolvedTenantUid
}

// Synchronous read — called throughout the app after boot.
// Priority:
//   1. Hostname-resolved UID (authoritative on tenant subdomains)
//   2. localStorage override (admin on platform host, or dev testing)
//   3. Static config fallback (null in multi-tenant mode)
export function getTenantUid() {
  // Hostname resolution wins — if we're on a tenant subdomain, use it
  if (_resolvedTenantUid) return _resolvedTenantUid

  try {
    const override = localStorage.getItem(DEV_TENANT_KEY)
    if (override) return override
  } catch (err) {
    // localStorage unavailable — fall through to static config
  }
  return artistConfig.artist.uid
}

export function setDevTenantUid(uid) {
  try {
    if (uid) localStorage.setItem(DEV_TENANT_KEY, uid)
    else localStorage.removeItem(DEV_TENANT_KEY)
  } catch (err) {
    // Ignore — override simply won't persist
  }
}

export function clearDevTenantUid() {
  setDevTenantUid(null)
}

// Resolve a `hostnames` doc to its canonical PRODUCTION host ('custom' keys are already full
// hostnames). Never use the window's base: dev saves write production Firestore, and
// window-derived `vangogh.localhost` targets caused the Jul 2026 stale-edge incidents.
export function hostnameDocToHost(key, type) {
  if (type !== 'subdomain') return key // custom domain: key is already the full hostname
  return `${key}.${PRODUCTION_HOST}`
}

// Returns true when the current hostname is a tenant subdomain
// (i.e. resolveTenant found a UID from the hostname).
// Use for layout decisions (which navbar/footer to show).
// Separate from getTenantUid() which also considers localStorage.
export function isTenantHost() {
  return !!_resolvedTenantUid
}

// Synchronous preliminary check — same logic as resolveHostTarget but without
// the Firestore call. Used to set platformMode before resolveTenant() completes
// so the correct navbar renders immediately without a layout flash.
export function hostnameHasTenant(hostname = window.location.hostname) {
  if (!hostname || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) return false
  if (extractSubdomain(hostname)) return true
  // Unknown hostname (not a platform host, not an IP) → treat as custom domain
  return !PLATFORM_HOSTS.includes(hostname)
}

// For testing: reset the resolved state
export function _resetResolvedTenant() {
  _resolvedTenantUid = null
  _tenantResolved = false
}
